import { useCallback, useEffect, useMemo, useState } from 'react';

import { execMeetingAPI } from '../../api/client';
import { confirmDialog } from '../../components/AppDialog';
import { COLORS, RADIUS, badge, btn, card, input as inputStyle } from '../../utils/uiStyles';
import ImportPanel from './ImportPanel';

// 임원회의 관리자 탭(#3130 5·6번) — «임원회의 관리»(execmeeting-admin) 권한이 있을 때만 ExecMeetingPage 가 붙인다.
//   ① 부서 목록: 조직도에서 골라 추가 · 직접 추가 · 이름 고치기 · 순서 · 사용/사용 안 함 · 지우기(보고서 0건일 때만)
//   ② PDF 가져오기(#3130 2번): 옛 통합 주간 보고서 PDF → 미리보기 → 아직 없는 주차·부서만 넣기(펼칠 때만 ImportPanel 을 붙인다)
//   ③ 접근권한: 다른 사용자에게 임원회의 메뉴(menu_access.execmeeting)를 부여·회수
// 서버 문구(409 등)는 그대로 보인다 — 서버가 판정의 정본이다(routers/exec_meeting_admin.py).

const labelStyle = { display: 'block', marginBottom: '6px', color: COLORS.textSub, fontSize: '12px', fontWeight: 700 };
const thStyle = { padding: '8px', textAlign: 'left', borderBottom: `1px solid ${COLORS.border}`, color: COLORS.textSub, fontSize: '12px', whiteSpace: 'nowrap' };
const tdStyle = { padding: '8px', borderBottom: `1px solid ${COLORS.borderSoft}`, verticalAlign: 'middle' };

function errorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && typeof detail[0]?.msg === 'string') return detail[0].msg;
  return fallback;
}

function Notice({ notice }) {
  if (!notice) return null;
  const error = notice.type === 'error';
  return (
    <div role={error ? 'alert' : 'status'} style={{
      marginBottom: '12px', padding: '10px 12px', borderRadius: RADIUS.md, fontSize: '13px',
      color: error ? COLORS.dangerText : COLORS.successText,
      background: error ? COLORS.dangerSoft : COLORS.successSoft,
    }}>{notice.text}</div>
  );
}

// 지우기 단추 사유 — 서버는 삭제된 보고서까지 세어 0건일 때만 지운다.
function deleteBlockReason(row) {
  if ((row.report_count || 0) > 0) return '보고서가 있음';
  if ((row.deleted_report_count || 0) > 0) return '삭제된 보고서가 있음';
  return '';
}

function DepartmentsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState(null);
  const [orgOpen, setOrgOpen] = useState(false);
  const [orgRows, setOrgRows] = useState(null);
  const [orgError, setOrgError] = useState('');
  const [orgQuery, setOrgQuery] = useState('');

  const reload = useCallback(async () => {
    try {
      const response = await execMeetingAPI.admin.departments();
      setRows(Array.isArray(response.data) ? response.data : []);
      setLoadError('');
    } catch (error) {
      setLoadError(errorMessage(error, '부서 목록을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const existingNames = useMemo(() => new Set(rows.map((row) => row.name)), [rows]);

  const run = async (action, successText) => {
    setBusy(true);
    setNotice(null);
    try {
      await action();
      setNotice({ type: 'success', text: successText });
      await reload();
      return true;
    } catch (error) {
      setNotice({ type: 'error', text: errorMessage(error, '요청을 처리하지 못했습니다.') });
      await reload();
      return false;
    } finally {
      setBusy(false);
    }
  };

  const addDirect = async (event) => {
    event.preventDefault();
    const name = newName.trim();
    if (!name) {
      setNotice({ type: 'error', text: '부서 이름을 입력해 주세요.' });
      return;
    }
    if (await run(() => execMeetingAPI.admin.addDepartment({ name }), `«${name}» 부서를 추가했습니다.`)) setNewName('');
  };

  const openOrg = async () => {
    const next = !orgOpen;
    setOrgOpen(next);
    if (!next || orgRows !== null) return;
    try {
      const response = await execMeetingAPI.admin.orgDepartments();
      setOrgRows(Array.isArray(response.data) ? response.data : []);
      setOrgError('');
    } catch (error) {
      setOrgRows(null);
      setOrgError(errorMessage(error, '조직도를 불러오지 못했습니다.'));
    }
  };

  const addFromOrg = (org) => run(
    () => execMeetingAPI.admin.addDepartment({ name: org.name, org_code: org.code || null }),
    `«${org.name}» 부서를 조직도에서 추가했습니다.`,
  );

  const saveName = async (row) => {
    const name = (editing?.name || '').trim();
    if (!name) {
      setNotice({ type: 'error', text: '부서 이름을 입력해 주세요.' });
      return;
    }
    if (name === row.name) {
      setEditing(null);
      return;
    }
    if (await run(() => execMeetingAPI.admin.updateDepartment(row.id, { name }), `«${row.name}» 부서 이름을 «${name}»(으)로 바꿨습니다.`)) {
      setEditing(null);
    }
  };

  // 위/아래 — 바꾼 뒤 순서를 10·20·30… 으로 다시 매기고 달라진 행만 보낸다(같은 순서 값이 섞여 있어도 움직인다).
  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    [next[index], next[target]] = [next[target], next[index]];
    const updates = next
      .map((row, position) => ({ row, order: (position + 1) * 10 }))
      .filter(({ row, order }) => row.sort_order !== order);
    run(async () => {
      for (const { row, order } of updates) {
        await execMeetingAPI.admin.updateDepartment(row.id, { sort_order: order });
      }
    }, '순서를 바꿨습니다.');
  };

  const toggleActive = (row) => run(
    () => execMeetingAPI.admin.updateDepartment(row.id, { active: !row.active }),
    row.active ? `«${row.name}» 부서를 사용 안 함으로 바꿨습니다.` : `«${row.name}» 부서를 다시 사용합니다.`,
  );

  const remove = async (row) => {
    const ok = await confirmDialog(`«${row.name}» 부서를 목록에서 지울까요?`);
    if (!ok) return;
    run(() => execMeetingAPI.admin.removeDepartment(row.id), `«${row.name}» 부서를 지웠습니다.`);
  };

  const term = orgQuery.trim();
  const orgMatches = (orgRows || []).filter((org) => !term
    || String(org.name || '').includes(term) || String(org.parent_name || '').includes(term));

  return (
    <section aria-labelledby="exec-admin-depts-heading" style={{ ...card, marginBottom: '14px' }}>
      <h3 id="exec-admin-depts-heading" style={{ margin: '0 0 6px', color: COLORS.text, fontSize: '16px' }}>부서 목록</h3>
      <p style={{ margin: '0 0 12px', color: COLORS.textSub, fontSize: '12px' }}>
        제출 탭의 부서 선택지와 통합 탭의 제출 현황이 이 목록을 따릅니다. 사용 안 함으로 바꾼 부서는 두 곳에서 빠집니다.
        이름을 바꾸면 그 이름으로 낸 보고서도 새 이름으로 보입니다. 별칭(DX본부→AX실 · 운영팀→경영혁신팀)과 겹치는 이름은 통합 탭에서 합쳐 보입니다.
      </p>
      <Notice notice={notice} />

      <form onSubmit={addDirect} style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '10px' }}>
        <label style={{ flex: '1 1 220px', minWidth: 0 }}>
          <span style={labelStyle}>직접 추가</span>
          <input aria-label="새 부서 이름" value={newName} maxLength={64} disabled={busy} onChange={(event) => setNewName(event.target.value)} placeholder="부서 이름(64자 이하)" style={inputStyle} />
        </label>
        <button type="submit" disabled={busy} style={btn('primary')}>직접 추가</button>
        <button type="button" disabled={busy} aria-expanded={orgOpen} onClick={openOrg} style={btn('secondary')}>조직도에서 추가</button>
      </form>

      {orgOpen && (
        <div aria-label="조직도 부서 목록" role="region" style={{ marginBottom: '12px', padding: '10px', border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.md }}>
          <input aria-label="조직도 부서 검색" value={orgQuery} onChange={(event) => setOrgQuery(event.target.value)} placeholder="부서·상위 부서 이름으로 찾기" style={{ ...inputStyle, marginBottom: '8px' }} />
          {orgError && <div role="alert" style={{ color: COLORS.dangerText, fontSize: '13px' }}>{orgError}</div>}
          {orgRows === null && !orgError && <div role="status" style={{ color: COLORS.textSub, fontSize: '13px' }}>조직도를 불러오는 중입니다.</div>}
          {orgRows !== null && orgMatches.length === 0 && <div style={{ color: COLORS.textMuted, fontSize: '13px' }}>맞는 조직도 부서가 없습니다.</div>}
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: '240px', overflowY: 'auto' }}>
            {orgMatches.map((org, index) => {
              const exists = existingNames.has(String(org.name || '').trim());
              return (
                <li key={`${org.code || ''}-${org.name}-${index}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', padding: '5px 0', borderBottom: `1px solid ${COLORS.borderSoft}` }}>
                  <span style={{ fontSize: '13px', color: COLORS.text }}>
                    {org.name}
                    {org.parent_name && <span style={{ marginLeft: '6px', color: COLORS.textMuted, fontSize: '12px' }}>{org.parent_name}</span>}
                  </span>
                  <button type="button" aria-label={`조직도 ${org.name} 추가`} disabled={busy || exists} title={exists ? '이미 목록에 있는 부서입니다' : undefined} onClick={() => addFromOrg(org)} style={btn('secondary', 'sm')}>
                    {exists ? '이미 있음' : '추가'}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {loading && <div role="status" style={{ color: COLORS.textSub, fontSize: '13px' }}>부서 목록을 불러오는 중입니다.</div>}
      {loadError && <div role="alert" style={{ color: COLORS.dangerText, fontSize: '13px' }}>{loadError}</div>}
      {!loading && !loadError && rows.length === 0 && <div style={{ color: COLORS.textMuted, fontSize: '13px' }}>등록된 부서가 없습니다.</div>}
      {rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table aria-label="임원회의 부서 목록" style={{ width: '100%', minWidth: '640px', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr>
                {['이름', '조직도 코드', '순서', '사용 여부', '보고서 수', '관리'].map((label) => <th key={label} scope="col" style={thStyle}>{label}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const isEditing = editing?.id === row.id;
                const blocked = deleteBlockReason(row);
                return (
                  <tr key={row.id}>
                    <td style={tdStyle}>
                      {isEditing ? (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <input aria-label={`${row.name} 새 이름`} value={editing.name} maxLength={64} disabled={busy} onChange={(event) => setEditing({ id: row.id, name: event.target.value })} style={{ ...inputStyle, minWidth: '140px' }} />
                          <button type="button" disabled={busy} onClick={() => saveName(row)} style={btn('primary', 'sm')}>저장</button>
                          <button type="button" disabled={busy} onClick={() => setEditing(null)} style={btn('secondary', 'sm')}>취소</button>
                        </div>
                      ) : (
                        <span style={{ color: COLORS.text, fontWeight: 600 }}>{row.name}</span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, color: COLORS.textSub }}>{row.org_code || '—'}</td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <span style={{ minWidth: '28px', color: COLORS.textSub }}>{row.sort_order}</span>
                        <button type="button" aria-label={`${row.name} 위로`} disabled={busy || index === 0} onClick={() => move(index, -1)} style={btn('secondary', 'sm')}>↑</button>
                        <button type="button" aria-label={`${row.name} 아래로`} disabled={busy || index === rows.length - 1} onClick={() => move(index, 1)} style={btn('secondary', 'sm')}>↓</button>
                      </div>
                    </td>
                    <td style={tdStyle}><span style={badge(row.active ? 'success' : 'default')}>{row.active ? '사용' : '사용 안 함'}</span></td>
                    <td style={tdStyle}>
                      {row.report_count || 0}
                      {(row.deleted_report_count || 0) > 0 && <span style={{ marginLeft: '4px', color: COLORS.textMuted, fontSize: '12px' }}>(삭제 {row.deleted_report_count})</span>}
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                        {!isEditing && (
                          <button type="button" aria-label={`${row.name} 이름 고치기`} disabled={busy} onClick={() => setEditing({ id: row.id, name: row.name })} style={btn('secondary', 'sm')}>이름 고치기</button>
                        )}
                        <button type="button" aria-label={`${row.name} ${row.active ? '사용 안 함' : '사용'}`} disabled={busy} onClick={() => toggleActive(row)} style={btn('secondary', 'sm')}>
                          {row.active ? '사용 안 함' : '사용'}
                        </button>
                        <button type="button" aria-label={`${row.name} 지우기`} disabled={busy || Boolean(blocked)} title={blocked || undefined} onClick={() => remove(row)} style={btn('danger', 'sm')}>지우기</button>
                        {blocked && <span style={{ color: COLORS.textMuted, fontSize: '11px' }}>{blocked}</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// 부여·회수 단추를 막는 사유 — 서버(PUT /exec-meeting/admin/access/{id})의 403 과 같은 셋.
export function accessLockReason(row) {
  if (row.role === 'admin') return '시스템 관리자는 항상 볼 수 있습니다';
  if (row.is_self) return '본인의 권한은 바꿀 수 없습니다';
  if (row.is_exec_admin) return '관리자 권한은 사용자 관리 화면에서';
  return '';
}

function AccessPanel() {
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [notice, setNotice] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const search = useCallback(async (term) => {
    setLoading(true);
    try {
      const response = await execMeetingAPI.admin.users(term);
      setRows(Array.isArray(response.data) ? response.data : []);
      setLoadError('');
    } catch (error) {
      setLoadError(errorMessage(error, '사용자 목록을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { search(''); }, [search]);

  const submitSearch = (event) => {
    event.preventDefault();
    search(query.trim());
  };

  const toggle = async (row) => {
    const granted = !row.has_access;
    setBusyId(row.id);
    setNotice(null);
    try {
      const response = await execMeetingAPI.admin.setAccess(row.id, granted);
      const updated = response.data && typeof response.data === 'object' ? response.data : {};
      setRows((current) => current.map((item) => (item.id === row.id ? { ...item, ...updated, has_access: updated.has_access ?? granted } : item)));
      setNotice({
        type: 'success',
        text: `${row.name} 님의 임원회의 권한을 ${granted ? '부여' : '회수'}했습니다. 그 사용자가 다시 로그인하면 메뉴에 반영됩니다.`,
      });
    } catch (error) {
      setNotice({ type: 'error', text: errorMessage(error, '권한을 바꾸지 못했습니다.') });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section aria-labelledby="exec-admin-access-heading" style={card}>
      <h3 id="exec-admin-access-heading" style={{ margin: '0 0 6px', color: COLORS.text, fontSize: '16px' }}>접근권한</h3>
      <p style={{ margin: '0 0 12px', color: COLORS.textSub, fontSize: '12px' }}>
        임원회의 메뉴를 볼 수 있는 사람을 정합니다. 임원회의 관리 권한은 사용자 관리 화면에서 시스템 관리자가 줍니다.
      </p>
      <Notice notice={notice} />
      <form onSubmit={submitSearch} style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginBottom: '10px' }}>
        <label style={{ flex: '1 1 240px', minWidth: 0 }}>
          <span style={labelStyle}>사용자 찾기</span>
          <input aria-label="사용자 검색" value={query} maxLength={100} onChange={(event) => setQuery(event.target.value)} placeholder="이름 또는 이메일" style={inputStyle} />
        </label>
        <button type="submit" disabled={loading} style={btn('secondary')}>검색</button>
      </form>
      {loading && <div role="status" style={{ color: COLORS.textSub, fontSize: '13px' }}>사용자 목록을 불러오는 중입니다.</div>}
      {loadError && <div role="alert" style={{ color: COLORS.dangerText, fontSize: '13px' }}>{loadError}</div>}
      {!loading && !loadError && rows.length === 0 && <div style={{ color: COLORS.textMuted, fontSize: '13px' }}>맞는 사용자가 없습니다.</div>}
      {rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table aria-label="임원회의 접근권한" style={{ width: '100%', minWidth: '560px', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr>
                {['이름', '이메일', '권한', '변경'].map((label) => <th key={label} scope="col" style={thStyle}>{label}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const reason = accessLockReason(row);
                return (
                  <tr key={row.id}>
                    <td style={{ ...tdStyle, color: COLORS.text, fontWeight: 600 }}>{row.name}</td>
                    <td style={{ ...tdStyle, color: COLORS.textSub }}>{row.email}</td>
                    <td style={tdStyle}>
                      <span style={badge(row.has_access ? 'success' : 'default')}>{row.has_access ? '권한 있음' : '권한 없음'}</span>
                      {row.is_exec_admin && <span style={{ ...badge('warn'), marginLeft: '4px' }}>관리자</span>}
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          aria-label={`${row.name} ${row.has_access ? '회수' : '부여'}`}
                          disabled={Boolean(reason) || busyId !== null}
                          title={reason || undefined}
                          onClick={() => toggle(row)}
                          style={btn(row.has_access ? 'danger' : 'primary', 'sm')}
                        >
                          {row.has_access ? '회수' : '부여'}
                        </button>
                        {reason && <span style={{ color: COLORS.textMuted, fontSize: '11px' }}>{reason}</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// PDF 가져오기 — 펼칠 때만 ImportPanel 을 마운트한다(탭 첫 렌더에는 가져오기 API 호출 0).
function ImportSection() {
  const [open, setOpen] = useState(false);
  return (
    <section aria-labelledby="exec-admin-import-heading" style={{ ...card, marginBottom: '14px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
        <h3 id="exec-admin-import-heading" style={{ margin: 0, color: COLORS.text, fontSize: '16px' }}>PDF 가져오기</h3>
        <button type="button" aria-expanded={open} aria-controls="exec-admin-import-panel" aria-label={`PDF 가져오기 ${open ? '접기' : '펼치기'}`} onClick={() => setOpen((value) => !value)} style={btn('secondary', 'sm')}>
          {open ? '접기' : '펼치기'}
        </button>
      </div>
      <p style={{ margin: '0 0 12px', color: COLORS.textSub, fontSize: '12px' }}>
        옛 통합 주간 보고서 PDF 를 읽어 부서별 미리보기를 만들고, 고친 뒤 고른 카드만 그 주차 보고서로 넣습니다. 그 주차에 이미 있는 부서는 건너뛰고 기존 보고서는 바꾸지 않습니다.
      </p>
      {open && <div id="exec-admin-import-panel"><ImportPanel /></div>}
    </section>
  );
}

export default function AdminTab() {
  return (
    <div>
      <DepartmentsPanel />
      <ImportSection />
      <AccessPanel />
    </div>
  );
}
